import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$findMatchingParent} from '@lexical/utils';
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_HIGH,
  KEY_DOWN_COMMAND,
} from 'lexical';
import {useEffect} from 'react';

import {$isEnhancedTableCellNode} from './EnhancedTableCellNode';

/**
 * Plugin to handle Cmd+A (Select All) behavior within table cells.
 * When inside a table cell, Cmd+A will only select content within that cell,
 * rather than selecting the entire document.
 */
export const TableCellSelectAllPlugin = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const removeKeyDownListener = editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        // Check if this is a Cmd+A (Mac) or Ctrl+A (Windows/Linux) event
        const isSelectAll =
          (event.key === 'a' || event.key === 'A') && (event.metaKey || event.ctrlKey);

        if (!isSelectAll) {
          return false; // Let other handlers process this event
        }

        // Get the current selection
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) {
          return false;
        }

        // Find the table cell ancestor of the current selection
        const anchorNode = selection.anchor.getNode();
        const tableCellNode = $findMatchingParent(anchorNode, $isEnhancedTableCellNode);

        if (!tableCellNode) {
          return false; // Not in a table cell, let default behavior happen
        }

        // Prevent default browser behavior
        event.preventDefault();
        event.stopPropagation();

        // Select all content within the table cell
        const cellKey = tableCellNode.getKey();

        editor.update(() => {
          const cell = editor.getElementByKey(cellKey);
          if (cell) {
            // Get the first and last child of the cell
            const children = tableCellNode.getChildren();

            if (children.length === 0) {
              return;
            }

            // Filter out decorator nodes and get actual content nodes
            const contentChildren = children.filter((child) => {
              return child.getType() !== 'TableCellDecorator';
            });

            if (contentChildren.length === 0) {
              return;
            }

            const firstChild = contentChildren[0];
            const lastChild = contentChildren[contentChildren.length - 1];

            // Create a selection that spans the entire cell content
            const newSelection = $getSelection();
            if ($isRangeSelection(newSelection)) {
              // Select from the start of the first child to the end of the last child
              newSelection.anchor.set(firstChild.getKey(), 0, 'element');

              // Determine the end offset based on the node type
              let endOffset = 0;
              if ($isTextNode(lastChild)) {
                endOffset = lastChild.getTextContentSize();
              } else if ($isElementNode(lastChild)) {
                endOffset = lastChild.getChildrenSize();
              }

              newSelection.focus.set(
                lastChild.getKey(),
                endOffset,
                $isTextNode(lastChild) ? 'text' : 'element'
              );
            }
          }
        });

        return true; // Mark event as handled
      },
      COMMAND_PRIORITY_HIGH
    );

    return () => {
      removeKeyDownListener();
    };
  }, [editor]);

  return null;
};
